// Transporte autenticado del modo AWS: adjunta el ID token de Cognito y, ante un
// 401, refresca UNA vez y reintenta. G-277.
//
// Por qué existe el reintento: el token dura 60 min y `CognitoUserSession.isValid()`
// no aplica margen alguno, así que una petición firmada en el filo del vencimiento
// llega expirada a PostgREST; y la rama `transient` del adaptador (G-273) entrega a
// propósito el último token bueno aunque esté vencido, para no confundir un
// parpadeo de red con un logout. En los dos casos el 401 es recuperable y la
// respuesta correcta es refrescar y reintentar — NO mandar al usuario al login,
// que es lo que pasaba: el error subía crudo hasta `vue-unmount-error.client.ts`,
// que reconoce `PGRST303`/«JWT expired» y redirige a /auth/login.
//
// El refresco va single-flight en el adaptador (`refreshIdToken`), así que veinte
// peticiones en paralelo que se topen con el mismo 401 comparten UN refresco.

export interface AuthedFetchDeps {
  /** Token actual (puede venir vencido: ver la rama `transient` de G-273). */
  getToken: () => Promise<string | null>
  /** Refresco forzado y single-flight. Devuelve el token nuevo o null. */
  refreshToken: () => Promise<string | null>
  fetchImpl?: typeof fetch
}

/**
 * Solo se reintenta lo que se puede reenviar con seguridad:
 * - un `Request` ya construido pudo consumir su cuerpo en el primer intento;
 * - un cuerpo `ReadableStream` no se puede releer.
 * En ambos casos se devuelve el 401 tal cual — mejor un error honesto que un
 * reintento que manda un cuerpo vacío.
 */
function isRetryable(input: RequestInfo | URL, init?: RequestInit): boolean {
  const inputOk = typeof input === 'string' || input instanceof URL
  const body = init?.body
  const bodyOk = !(typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
  return inputOk && bodyOk
}

export function createAuthedFetch(deps: AuthedFetchDeps): typeof fetch {
  const doFetch = deps.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a))

  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = await deps.getToken()
    const headers = new Headers(init?.headers)
    if (token) headers.set('Authorization', `Bearer ${token}`)

    const res = await doFetch(input as any, { ...init, headers })
    if (res.status !== 401 || !isRetryable(input, init)) return res

    const fresh = await deps.refreshToken()
    // Sin token nuevo, o el mismo de antes: el 401 no era por vencimiento.
    // Se devuelve tal cual; cerrar sesión NO es decisión del transporte.
    if (!fresh || fresh === token) return res

    const retryHeaders = new Headers(init?.headers)
    retryHeaders.set('Authorization', `Bearer ${fresh}`)
    return doFetch(input as any, { ...init, headers: retryHeaders })
  }
}
